import React, { useEffect, useState } from 'react';
import ImportBankFile from '../components/ImportBankFile';
import { useReconciliationStore } from '../store/reconciliationStore';
import { getMatchSuggestions } from '../utils/simpleMatcher';
import { fetchInvoices } from '../../../services/InvoiceService';
import { fetchExpenses } from '../../../services/BillsExpensesService';
import { useAuth } from '../../../../AuthProvider';
import ReconciliationActions from '../components/ReconciliationActions';
import { Table, Card, Badge, Typography, Empty, Alert } from 'antd';
import { FileDoneOutlined, FileExcelOutlined, CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
const { Title, Text } = Typography;




const BankReconciliationPage = () => {
  const { transactions, selectedTransaction, setSelectedTransaction, setSuggestions, setTransactions } = useReconciliationStore();
  const auth = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);

  const [tnxSug, setTnxSug] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all invoices and expenses on mount
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [invRes, expRes] = await Promise.all([
          fetchInvoices(auth.token),
          fetchExpenses(auth.token,auth.additionalUserInfo.companyId)
        ]);
        setInvoices(invRes.data || []);
        setExpenses(expRes.data || []);
      } catch (e) {
        setInvoices([]);
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Update reconciliation status for all transactions based on match suggestions
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      const updated = transactions.map(tx => {
        const suggestions = getMatchSuggestions(tx, invoices, expenses);
        let reconciliationStatus = 'UNMATCHED';
        if (tx.reconciliationStatus === 'RECONCILED') {
          reconciliationStatus = 'RECONCILED';
        } else if (suggestions && suggestions.length > 0) {
          reconciliationStatus = 'MATCHED';
        }
        return {
          ...tx,
          reconciliationStatus,
        };
      });
      setTnxSug(updated);
      // If a transaction is selected, update its suggestions
      if (selectedTransaction) {
        const sel = updated.find(t => t.id === selectedTransaction.id);
        const selSuggestions = sel ? getMatchSuggestions(sel, invoices, expenses) : [];
        setSuggestions(selSuggestions);
      } else {
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  }, [transactions]);


  useEffect(() => {
    if (selectedTransaction) {

      const sel = tnxSug.find(
        t => t.id === selectedTransaction.id
      );

      const selSuggestions = sel
        ? getMatchSuggestions(sel, invoices, expenses)
        : [];
      //console.log('Selected Transaction:', sel);
      //console.log('Match Suggestions:', selSuggestions);
      setSuggestions(selSuggestions);

    } else {
      setSuggestions([]);
    }

  }, [
    selectedTransaction,
    tnxSug,
    invoices,
    expenses
  ]);
  // Ant Design Table columns
  const columns = [
    {
      title: 'Date',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      render: (date) => <Text>{date}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc) => <Text strong>{desc}</Text>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (amt) => <Text>${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'reconciliationStatus',
      key: 'reconciliationStatus',
      render: (status) => {
        if (status === 'RECONCILED') return <Badge status="success" text="Reconciled" />;
        if (status === 'MATCHED') return <Badge status="warning" text="Matched" />;
        return <Badge status="processing" text="Unmatched" />;
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: '100%', background: '#f4f6fa' }}>
      {/* Left: Bank Transaction List */}
      <div style={{ width: '54%', borderRight: '1px solid #e5e7eb', padding: 24, background: '#fff', overflowY: 'auto' }}>
        <Card bordered={false} style={{ marginBottom: 16, background: '#f0f5ff' }}>
          <Title level={4} style={{ margin: 0 }}><FileExcelOutlined /> Bank Statement Import</Title>
          <ImportBankFile onImport={setTransactions} />
        </Card>
        <Title level={5} style={{ marginBottom: 12 }}>Bank Transactions</Title>
        <Table
          dataSource={tnxSug}
          columns={columns}
          rowKey="id"
          size="middle"
          rowClassName={(record) => (selectedTransaction?.id === record.id ? 'ant-table-row-selected' : '')}
          onRow={(record) => ({
            onClick: () => { setSelectedTransaction(record.id);setSelectedRecord(record);},
            style: { cursor: 'pointer' },
          })}
          pagination={false}
          locale={{ emptyText: <Empty description="Upload a bank file to begin" image={<FileExcelOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />} /> }}
        />
      </div>
      {/* Right: Details/Actions */}
      <div style={{ width: '46%', padding: 24, background: '#f9fafb' }}>
        {selectedTransaction ? (
          <Card
            title={<span><FileDoneOutlined /> TransactiSSSSon Details</span>}
            bordered={false}
            style={{ marginBottom: 16, boxShadow: '0 2px 8px #f0f1f2' }}
            extra={selectedRecord.reconciliationStatus === 'RECONCILED' ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : selectedRecord.reconciliationStatus === 'MATCHED' ? <CheckCircleTwoTone twoToneColor="#faad14" /> : <CloseCircleTwoTone twoToneColor="#faad14" />}
          >
            <div style={{ marginBottom: 8 }}><Text type="secondary">Date:</Text> <Text>{selectedRecord.transactionDate}</Text></div>
            <div style={{ marginBottom: 8 }}><Text type="secondary">Description:</Text> <Text strong>{selectedRecord.description}</Text></div>
            <div style={{ marginBottom: 8 }}><Text type="secondary">Amount:</Text> <Text>${Number(selectedRecord.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></div>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary">Status:</Text> {' '}
              {selectedRecord.reconciliationStatus === 'RECONCILED' && <Badge status="success" text="Reconciled" />}
              {selectedRecord.reconciliationStatus === 'MATCHED' && <Badge status="warning" text="Matched" />}
              {selectedRecord.reconciliationStatus !== 'RECONCILED' && selectedRecord.reconciliationStatus !== 'MATCHED' && <Badge status="processing" text="Unmatched" />}
            </div>
            <ReconciliationActions />
          </Card>
        ) : (
          <Alert message="Select a transaction to view details and suggestions." type="info" showIcon style={{ marginTop: 32 }} />
        )}
      </div>
    </div>
  );
};

export default BankReconciliationPage;
